import { useQueryClient } from '@tanstack/react-query';
import { CirclePlay, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { useModal } from '@/components/providers/ModalProvider/ModalProvider';
import { Button } from '@/components/shadcn/button';
import { Field, FieldLabel } from '@/components/shadcn/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import CreatePartyModal from '@/features/party/components/CreatePartyModal/CreatePartyModal';
import useResetOnboardingCompleted from '@/mutations/useResetOnboardingCompleted';
import useLocalSettings from '@/stores/useLocalSettings';

const THEMES = ['wegroup', 'THOMA', 'YOUSURE'];

export default function DevSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const overwriteTheme = useLocalSettings((state) => state.overwriteTheme);
  const { setOverwriteTheme } = useLocalSettings((state) => state.actions);

  const { mutateAsync: resetOnboarding } = useResetOnboardingCompleted();

  const handleResetOnboarding = async () => {
    await resetOnboarding();
    navigate('/assistant');
  };

  const handleClearReactQuery = () => queryClient.clear();

  const { email, party } = useEmail();
  const { showModal } = useModal();

  const handleCreatePartyForEmail = () => {
    party.startPartyExtractionFlow();
    showModal(CreatePartyModal, {}, { title: t('CREATE_PARTY_MODAL_TITLE') });
  };

  if (!import.meta.env.DEV) return null;

  return (
    <div className="flex flex-col gap-1 p-2">
      <FieldLabel>Developer Settings</FieldLabel>

      <div className="flex flex-col gap-4 rounded-lg border p-2">
        <Field>
          <FieldLabel>Overwrite Theme</FieldLabel>
          <Select
            value={overwriteTheme ?? 'wegroup'}
            onValueChange={setOverwriteTheme}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THEMES.map((theme) => (
                <SelectItem key={theme} value={theme}>
                  {theme.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Button variant="secondary" onClick={handleResetOnboarding}>
          <CirclePlay />

          {t('SETTINGS_START_TOUR')}
        </Button>

        <Button
          variant="secondary"
          onClick={handleCreatePartyForEmail}
          disabled={!email}
        >
          <UserPlus />
          Create relation for this email
        </Button>

        <Button
          variant={'destructive-secondary'}
          onClick={handleClearReactQuery}
        >
          Clear all React Query caches
        </Button>
      </div>
    </div>
  );
}
